import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Text, Flex, useToast, Skeleton, Switch, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { StoreFilterBox } from './components/ProductStoreFilterBox';
import { GetParamsObject } from '@/utils';
import { useDeleteProductStoreMutation, useGetProductStoreByUUID, useProductStores } from '@/services/queries/product-store';
import { ProductStoreTable } from './components/ProductStoreTable/ProductStoreTable';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { ProductStoreCreateModal } from './components/ProductStoreCreateModal';
import { ProductStoreEditModal } from './components/ProductStoreEditModal';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAllClub } from '@/services/queries/club';
import { ProductStore } from '@/model';
import { useAllPartners } from '@/services/queries/partner';
import { StoreProductsFilters } from './types';

export const ApplicationStoreProducts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.STORE_PRODUCTS);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_store.components.product_store.application_store_enable_success')
      : t('pages.application_store.components.product_store.application_store_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_store.components.product_store.application_store_enable_error')
      : t('pages.application_store.components.product_store.application_store_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.STORE_PRODUCTS, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const [selectedItem, setSelectedItem] = useState<ProductStore | undefined>(undefined);
  const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false);
  const [isShowCreateModal, setIsShowCreateModal] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  // filtro
  const filtersForm = useFiltersForm<StoreProductsFilters>({
    defaultValues: {
      team_uuid: '',
      title: '',
      start_date: '',
      end_date: '',
      status: '',
    },
  });

  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 30,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: partners = [] } = useAllPartners(true);
  const { data: clubs = [] } = useAllClub(true);
  const { data: productsData, isLoading: isLoadingProductData, forceRefetch } = useProductStores(params);

  const products = productsData?.data || [];

  const getItemByUUID = useGetProductStoreByUUID();
  const deleteMutation = useDeleteProductStoreMutation();

  const onHandleEdit = useCallback(
    (item: ProductStore) => {
      getItemByUUID.mutate(item.uuid, {
        onSuccess: res => {
          setSelectedItem(res);
          setIsShowEditModal(true);
        },
        onError: (resp: any) => {
          setSelectedItem(undefined);
          setIsShowEditModal(false);
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_store.components.product_store.application_store_product_not_found'),
          });
        },
      });
    },
    [getItemByUUID, toast, t],
  );

  const onHandleDelete = useCallback((item: ProductStore) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  }, []);

  const onHandleEditDone = useCallback(() => {
    setIsShowEditModal(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleDeleteConfirm = useCallback(() => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_store.components.product_store.application_store_product_delete_success'),
          status: 'success',
        });

        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_store.components.product_store.application_store_product_delete_error'),
        });
      },
    });
  }, [deleteMutation, forceRefetch, selectedItem, toast, t]);

  const onHandleCreateDone = useCallback(() => {
    setIsShowCreateModal(false);
    forceRefetch();
  }, [forceRefetch]);

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  useEffect(() => {
    if (location && location.state && location.state.product_uuid) {
      onHandleEdit({ uuid: location.state.product_uuid } as ProductStore);
      navigate(location.pathname, { state: { ...location.state, product_uuid: undefined } });
    }

    if (location && location.state && location.state.new_product) {
      setIsShowCreateModal(true);
      navigate(location.pathname, { state: { ...location.state, new_product: undefined } });
    }
  }, [location, navigate, onHandleEdit]);

  return (
    <PageContent title={t('pages.application_store.components.product_store.application_store_page_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_store.components.product_store.application_store_store_label')}
                </Text>

                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_store.components.product_store.application_store_enabled')
                    : t('pages.application_store.components.product_store.application_store_disabled')}
                </Switch>
              </>
            )}
          </Flex>

          <Flex gap={2} direction={{ base: 'column', md: 'row' }}>
            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => setIsShowCreateModal(true)}
              w={{ base: '100%', md: 'initial' }}
            >
              <ChakraFeatherIcon name='plus-circle' />
              {t('pages.application_store.components.product_store.application_store_add_product')}
            </Button>
          </Flex>
        </Flex>

        <SectionBox bodyProps={{ gap: 'small' }}>
          <StoreFilterBox clubs={clubs} filtersForm={filtersForm} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <Flex direction='column' gap='micro'>
            <ProductStoreTable
              data={products}
              isLoading={isLoadingProductData}
              sorting={sorting}
              pageNumber={pagination.pageNumber}
              pageSize={pagination.pageSize}
              totalPages={productsData?.meta.pages || 0}
              totalItems={productsData?.meta.total || 0}
              onSortingChange={(field, desc) => setSorting({ field, desc })}
              onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
              OnDelete={item => onHandleDelete(item)}
              OnEdit={item => onHandleEdit(item)}
            />
          </Flex>
        </Flex>
      </SectionBox>

      <ProductStoreCreateModal
        partners={partners}
        isOpen={isShowCreateModal}
        onClose={() => setIsShowCreateModal(false)}
        onDone={onHandleCreateDone}
      />

      {selectedItem && (
        <ProductStoreEditModal
          partners={partners}
          isOpen={!!isShowEditModal}
          onClose={() => {
            setIsShowEditModal(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_store.components.product_store.application_store_delete_product_title')}
        description={
          <Text>
            {t('pages.application_store.components.product_store.application_store_delete_product_confirm')}{' '}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.title}"
            </Text>
            ?
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_store.components.product_store.application_store_confirm_modal_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_store.components.product_store.application_store_confirm_enable')
              : t('pages.application_store.components.product_store.application_store_confirm_disable')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea || getItemByUUID.isLoading || deleteMutation.isLoading} />
    </PageContent>
  );
};
